<svelte:options accessors/>
<script lang="ts">
    import NauiExplorer from "../../../naui/pages/NauiExplorer.svelte";
    import NauiButton from "../../../naui/atoms/NauiButton.svelte";
    import NauiTag from "../../../naui/atoms/NauiTag.svelte";
    import NauiInput from "../../../naui/atoms/NauiInput.svelte";
    import NauiSlideOver from "../../../naui/layout/NauiSlideOver.svelte";
    import NauiTable from "../../../naui/components/NauiTable.svelte";
    import {getFormatter, getSubtotal, getTotal} from "../../../utils";
    import {createEventDispatcher} from "svelte";
    import Margen from "./Margen.svelte";

    interface IPackaging {
        special_boxes: IPackagingItem,
        packaged_boxes: IPackagingItem,
        box_unpacking: IPackagingItem,
        furniture_unpacking: IPackagingItem
    }

    interface IPackagingItem {
        price: number,
        tax: number,
        quantity?: number
    }

    export let data;
    export let packaging: IPackaging;

    export let columns;
    export let margin = 30;

    let open = false;
    let dispatch = createEventDispatcher();
    let formatter = getFormatter();
    let enabledColumns = [
        {
            slug: 'name',
            label: 'Materiales',
            type: 'text',
            order: false
        },
        {
            slug: 'quantity',
            label: 'Cantidad',
            type: 'text',
            order: false
        },
        {
            slug: 'value',
            label: 'Valor',
            type: 'text',
            order: false
        },
        {
            slug: 'actions',
            label: '',
            type: 'actions',
            order: false
        }
    ];

    $: marginPercent = (margin / 100) + 1;

    function calcPackagingTotal(p: IPackaging, withTax: boolean) {
        if (!p) {
            return 0;
        }
        return getPrice(p.special_boxes, true, withTax) +
            getPrice(p.packaged_boxes, false, withTax) +
            getPrice(p.box_unpacking, false, withTax) +
            getPrice(p.furniture_unpacking, false, withTax);
    }

    function getPrice(obj, withMargin, withTax) {
        const objMargin = withMargin ? (1 + (margin / 100)) : 1
        const objTax = withTax ? (1 + obj.tax) : 1;
        return obj.price * objMargin / objTax;
    }

    $: packagingTotal = calcPackagingTotal(packaging, false);
    $: packagingSubtotal = calcPackagingTotal(packaging, true);

    $: enabled = data ? data.filter(d => d.quantity > 0) : [];
    $: subtotal = getSubtotal(enabled) * marginPercent;
    $: total = getTotal(enabled) * marginPercent;

    $: labelSubtotal = `Subtotal sin IVA | ${formatter(subtotal + packagingSubtotal)}`;
    $: labelTotal = `Subtotal con IVA | ${formatter(total + packagingTotal)}`;

    $: summaryData = setEnabled(data, packaging);

    let filtered = null;

    function add() {
        open = true;
    }

    function showImages() {
    }

    function setEnabled(d: Array<any>, p: IPackaging) {
        const enabled = [];
        if (d) {
            for (const item of d) {
                if (item.quantity > 0) {
                    enabled.push({
                        slug: item.id,
                        name: item.name,
                        quantity: item.quantity,
                        value: formatter(item.quantity * item.price),
                        ref: item,
                        actions: [{slug: 'remove', icon: 'times-circle'}]
                    });
                }
            }
        }
        if (p) {
            if (p.special_boxes.price > 0) {
                enabled.push({
                    slug: 'guacales',
                    name: 'Guacales',
                    quantity: p.special_boxes.quantity || '-',
                    value: formatter(p.special_boxes.price),
                    ref: p.special_boxes,
                    actions: [{slug: 'remove', icon: 'times-circle'}]
                });
            }
            if (p.packaged_boxes.price > 0) {
                enabled.push({
                    slug: 'cajas',
                    name: 'Cajas empacadas por Keep & Go',
                    quantity: p.packaged_boxes.quantity || '-',
                    value: formatter(p.packaged_boxes.price),
                    ref: p.packaged_boxes,
                    actions: [{slug: 'remove', icon: 'times-circle'}]
                });
            }
            if (p.box_unpacking.price > 0) {
                enabled.push({
                    slug: 'desempaque-cajas',
                    name: 'Desempaque de cajas',
                    quantity: p.box_unpacking.quantity || '-',
                    value: formatter(p.box_unpacking.price),
                    ref: p.box_unpacking,
                    actions: [{slug: 'remove', icon: 'times-circle'}]
                });
            }
            if (p.furniture_unpacking.price > 0) {
                enabled.push({
                    slug: 'desempaque-muebles',
                    name: 'Desempaque de muebles',
                    quantity: p.furniture_unpacking.quantity || '-',
                    value: formatter(p.furniture_unpacking.price),
                    ref: p.furniture_unpacking,
                    actions: [{slug: 'remove', icon: 'times-circle'}]
                });
            }
        }
        return enabled;
    }

    function changeData(ev) {
        const item = ev.detail.item;
        item.quantity = ev.detail.data;
        data = data;
    }

    function removeItem(ev) {
        const item = ev.detail.item;
        if (item.ref.id) {
            item.ref.quantity = 0;
            data = data;
        } else {
            item.ref.price = 0;
            packaging = packaging;
        }
    }

    function save() {
        dispatch('save', {
            data: data.filter(d => d.quantity > 0),
            packaging: packaging
        });
    }
</script>

<div>
    <NauiExplorer {data} {columns} on:action={(ev) => changeData(ev)}
                  hideSearch={true}
                  paginate={false} on:filter on:next>
        <div slot="actions">
            <div class="box-l sx-15">
                <div class="box-l sx-1 max-w-400px">
                    <Margen type="packaging" bind:margin={margin} on:changemargin/>
                </div>
                <div class="grid grid-4 gap-ii">
                    <NauiButton on:click={add} caption="AGREGAR A LA COTIZACIÓN" mode="stroked" icon="plus"/>
                    <NauiButton on:click={showImages} caption="VER IMAGES DEL SERVICIO" icon="image" mode="accent"/>
                    <NauiTag label={labelSubtotal}/>
                    <NauiTag label={labelTotal}/>
                </div>
            </div>
        </div>
    </NauiExplorer>

    <div class="p-1">
        <div class="box-l py-i">
            <div class="min-w-300px">Guacales</div>
            <div class="min-w-250px">
                <NauiInput bind:value={packaging.special_boxes.price} label="Precio total guacales" type="number"/>
            </div>
            <div class="box-l sx-1 ml-1">
                <NauiButton icon="minus-circle"
                            iconFamily="fal"
                            color="accent"
                            on:click={() => {packaging.special_boxes.quantity = Math.max(0, packaging.special_boxes.quantity - 1)}}/>
                <NauiInput bind:value={packaging.special_boxes.quantity}
                           label="Cantidad de guacales"
                           type="number"/>
                <NauiButton icon="plus-circle"
                            iconFamily="fal"
                            color="accent"
                            on:click={() => {packaging.special_boxes.quantity += 1}}/>
            </div>
        </div>
        <div class="box-l py-i">
            <div class="min-w-300px">Caja empacada por Keep & Go</div>
            <div class="min-w-250px">
                <NauiInput bind:value={packaging.packaged_boxes.price} label="Precio total por empaque" type="number"/>
            </div>
            <div class="box-l sx-1 ml-1">
                <NauiButton icon="minus-circle"
                            iconFamily="fal"
                            color="accent"
                            on:click={() => {packaging.packaged_boxes.quantity = Math.max(0, packaging.packaged_boxes.quantity - 1)}}/>
                <NauiInput bind:value={packaging.packaged_boxes.quantity}
                           label="Cantidad de cajas empacadas"
                           type="number"/>
                <NauiButton icon="plus-circle"
                            iconFamily="fal"
                            color="accent"
                            on:click={() => {packaging.packaged_boxes.quantity += 1}}/>
            </div>
        </div>
        <div class="box-l py-i">
            <div class="min-w-300px">Desempaque de cajas</div>
            <div class="min-w-250px">
                <NauiInput bind:value={packaging.box_unpacking.price} label="Precio total desempaque cajas (IVA incluido)"
                           type="number"/>
            </div>
        </div>
        <div class="box-l py-i">
            <div class="min-w-300px">Desempaque de muebles</div>
            <div class="min-w-250px">
                <NauiInput bind:value={packaging.furniture_unpacking.price}
                           label="Precio total desempaque muebles (IVA incluido)"
                           type="number"/>
            </div>
        </div>
    </div>
    <div class="p-1">
        <NauiButton on:click={() => open = true} caption="AGREGAR A LA COTIZACIÓN" mode="stroked" icon="plus"/>
    </div>
</div>

<NauiSlideOver bind:open={open} title="TOTAL MATERIALES EMBALAJE">
    <NauiTable data={summaryData} columns={enabledColumns} on:action={(ev) => removeItem(ev)}/>
    <div class="py-15">
        <h2 class="accent text-c w-100">{labelSubtotal}</h2>
        <h2 class="accent text-c w-100">{labelTotal}</h2>
    </div>
    <NauiButton on:click={save}
                color="accent"
                mode="solid"
                caption="AGREGAR A LA COTIZACIÓN"/>
</NauiSlideOver>